/**
 * Admin Studio v1 — AdminPlacesPage
 * Uses PageHeader, StudioSurface, EmptyBlock for consistent admin UI.
 * Responsive: table on desktop, card list on mobile.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Sparkles, MoreVertical } from "lucide-react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { PageHeader } from "@/components/admin/PageHeader";
import { StudioSurface } from "@/components/admin/StudioSurface";
import { EmptyBlock } from "@/components/admin/EmptyBlock";
import { AdminPlacesFilters } from "@/components/admin/AdminPlacesFilters";
import { AdminPlacesTableRow } from "@/components/admin/AdminPlacesTableRow";
import { PlaceRowCard } from "@/components/admin/PlaceRowCard";
import { useAdminPlacesList, type AdminPlaceRow } from "@/hooks/useAdminPlacesList";
import { useDeleteAdminPlace } from "@/hooks/useAdminPlaces";
import { useImprovePlaces } from "@/hooks/useImprovePlaces";

export default function AdminPlacesPage() {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [placeToDelete, setPlaceToDelete] = useState<AdminPlaceRow | null>(null);

  const {
    places,
    isLoading,
    filters,
    setSearch,
    setFilter,
    resetFilters,
    activeFilterCount,
    filterOptions,
  } = useAdminPlacesList();

  const deleteMutation = useDeleteAdminPlace();
  const { improve, isRunning } = useImprovePlaces();

  const handleDelete = () => {
    if (placeToDelete) {
      deleteMutation.mutate(placeToDelete.id);
      setDeleteDialogOpen(false);
      setPlaceToDelete(null);
    }
  };

  const onRequestDelete = (place: AdminPlaceRow) => {
    setPlaceToDelete(place);
    setDeleteDialogOpen(true);
  };

  return (
    <AdminLayout title="Platser">
      <PageHeader
        title="Platser"
        description={`${places.length} platser${activeFilterCount > 0 ? ` (filtrerat)` : ''}`}
        actions={
          <div className="flex gap-2">
            {/* Desktop: show both buttons */}
            <Button
              variant="outline"
              size="sm"
              className="hidden md:inline-flex"
              onClick={() => improve({ dryRun: false })}
              disabled={isRunning}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {isRunning ? "Förbättrar…" : "Förbättra"}
            </Button>
            <Button size="sm" onClick={() => navigate("/app/admin/places/new")}>
              <Plus className="h-4 w-4 mr-2" />
              <span className="hidden md:inline">Ny plats</span>
              <span className="md:hidden">Ny</span>
            </Button>

            {/* Mobile: overflow menu for secondary actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="md:hidden h-9 w-9 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover">
                <DropdownMenuItem
                  onClick={() => improve({ dryRun: false })}
                  disabled={isRunning}
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {isRunning ? "Förbättrar…" : "Förbättra"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      <div className="px-4 lg:px-8 pb-8 space-y-4">
        {/* Filters */}
        <AdminPlacesFilters
          filters={filters}
          setSearch={setSearch}
          setFilter={setFilter}
          resetFilters={resetFilters}
          activeFilterCount={activeFilterCount}
          filterOptions={filterOptions}
        />

        {/* Content */}
        <StudioSurface className="p-0 overflow-hidden">
          {isLoading ? (
            <div className="p-6 space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 rounded-lg bg-muted animate-pulse" />
              ))}
            </div>
          ) : places.length === 0 ? (
            <EmptyBlock
              title="Inga platser"
              description={
                activeFilterCount > 0
                  ? "Inga platser matchar dina filter."
                  : "Skapa din första plats för att komma igång."
              }
              action={
                activeFilterCount === 0 ? (
                  <Button onClick={() => navigate("/app/admin/places/new")}>
                    <Plus className="h-4 w-4 mr-2" />
                    Ny plats
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              {/* Desktop: table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[60px]">Bild</TableHead>
                      <TableHead>Namn</TableHead>
                      <TableHead className="hidden sm:table-cell">Ort</TableHead>
                      <TableHead className="hidden md:table-cell">Landskap</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right w-[180px]">Åtgärder</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {places.map((place) => (
                      <AdminPlacesTableRow
                        key={place.id}
                        place={place}
                        onDelete={onRequestDelete}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile: card list */}
              <div className="md:hidden">
                {places.map((place) => (
                  <PlaceRowCard
                    key={place.id}
                    place={place}
                    onDelete={onRequestDelete}
                  />
                ))}
              </div>
            </>
          )}
        </StudioSurface>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Radera plats?</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill radera platsen "{placeToDelete?.name}"?
              Detta kommer att ta bort alla kopplingar till producenter.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Radera
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
